<template>
    <div class="head">
        <div class="title">{{ title }}</div>
        <GoHomeButton v-if="isHomeButtonVisible" @click.prevent="onGoHomeButtonClick" />
        <GoBackButton v-if="isGoBackButtonVisible" @click.prevent="onGoBackButtonClick" />
        <UserMenuComponent />
        <LanguageSelector v-if="kiosk.isKioskMode" />
    </div>

    <template v-if="isKioskMode">
        <KioskOfferOverview @addToCartPopoverClick="onArticleClick" @quickAddClick="quickAddItem" />

        <div class="bottom">
            <div class="selectionsum">{{ selectedArticlesText }}</div>
            <PayButtonComponent
                :action="trans.general.continue"
                :amount="totalAmount"
                :disabled="!isPayButtonEnabled"
                @click="onProceedButtonClick"
            />
        </div>
    </template>
    <template v-if="!isKioskMode">
        <OperatorOfferOverview
            v-if="showOperatorOfferOverview"
            @addToCartPopoverClick="onArticleClick"
            @quickAddClick="quickAddItem"
        />

        <OfferOverview
            v-if="!showOperatorOfferOverview"
            ref="offerOverviewComponent"
            @articleClick="onArticleClick"
            @scrolledChanged="onScrolledChanged"
        />

        <div class="bottom">
            <div class="selectionsum">{{ selectedArticlesText }}</div>
            <PayButtonComponent
                :action="trans.general.continue"
                :amount="totalAmount"
                :disabled="!isPayButtonEnabled"
                @click="onProceedButtonClick"
            />
        </div>
    </template>
</template>

<script setup lang="ts">
    import { TastierClient } from "@/app/TastierClient";
    import { OrderController } from "@/app/controller/OrderController";
    import { trans } from "@/app/localization/Trans";
    import { AddToCardPopover } from "@/app/popover/AddToCardPopover";
    import { useKioskStore } from "@/app/stores/kiosk";
    import { Helper } from "@/app/ui/Helper";
    import { Popup } from "@/components/dialog/Popup";
    import { getPreselectedOptions } from "@/components/order/preselectedArticleOptions";
    import { useAddToCart } from "@/components/order/useAddToCart";
    import type { ICustomerArticle, ICustomerArticleWithOptions } from "@/data/model/Customer";
    import { useCartStore } from "@/data/stores/cart";
    import { useIdentityStore } from "@/data/stores/identity";
    import { useOfferStore } from "@/data/stores/offer";
    import { clientLanguage } from "@/shared/localization/language";
    import { logError } from "@/shared/logging/errors";
    import { toMoneyWithSign } from "@/shared/utils/format";
    import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";

    import PayButtonComponent from "@/app/components/footer/PayButtonComponent.vue";
    import GoBackButton from "@/app/components/header/GoBackButton.vue";
    import GoHomeButton from "@/app/components/header/GoHomeButton.vue";
    import UserMenuComponent from "@/app/components/header/UserMenuComponent.vue";
    import LanguageSelector from "@/app/components/language/LanguageSelector.vue";
    import KioskOfferOverview from "@/app/components/order/KioskOfferOverview.vue";
    import OfferOverview from "@/app/components/order/OfferOverview.vue";
    import OperatorOfferOverview from "@/app/components/order/OperatorOfferOverview.vue";

    const identity = useIdentityStore();
    const kiosk = useKioskStore();
    const offer = useOfferStore();
    const cart = useCartStore();

    // header
    const title = ref<string>();
    const isHomeButtonVisible = ref(true);
    const isGoBackButtonVisible = ref(false);

    const isViewVisible = ref(false);
    const isMounted = ref(false);
    const showOperatorOfferOverview = ref(false);
    const isKioskMode = computed(() => kiosk.isKioskMode);

    OrderController.onShown.add(onBecomeVisible);
    OrderController.onHidden.add(onBecomeInvisible);
    OrderController.onReset.add(reset);

    // html refs
    const offerOverviewComponent = ref<InstanceType<typeof OfferOverview> | null>(null);

    // there are some cases where the view is shown before the component is mounted
    // in this case we need to save the data and use it when the component is mounted
    let deferredBecomeVisibleData: { isMainNavigation: boolean; hasPreorderTimeStep?: boolean } | undefined;

    // init components
    onMounted(() => {
        constructor();
        isMounted.value = true;

        if (deferredBecomeVisibleData) {
            onBecomeVisible(deferredBecomeVisibleData);
            deferredBecomeVisibleData = undefined;
        }
    });

    onBeforeUnmount(() => {
        OrderController.onShown.remove(onBecomeVisible);
        OrderController.onHidden.remove(onBecomeInvisible);
        OrderController.onReset.remove(reset);
        TastierClient.context.onOfferChanged.remove(redrawList);
        TastierClient.context.onCartChanged.remove(updateCart);
        isMounted.value = false;
    });

    let addToCardPopover: AddToCardPopover | undefined;
    let needsOfferReload = true;

    const selectedArticles = ref(0);
    const selectedArticlesText = computed(() => trans.order.itemsSelected.replace("{0}", selectedArticles.value + ""));

    const totalAmount = ref<string>(toMoneyWithSign(0));
    const isPayButtonEnabled = ref(false);

    function constructor() {
        updateTitle();

        // init bottom
        totalAmount.value = toMoneyWithSign(0);
        isPayButtonEnabled.value = false;

        TastierClient.context.onOfferChanged.add(redrawList);
    }

    // operators (admins included) get the operator offer overview, everybody else the end user one
    async function updateOfferOverviewMode() {
        const previousShowOperatorOfferOverview = showOperatorOfferOverview.value;
        showOperatorOfferOverview.value = identity.hasOperatorAccess;
        await nextTick();

        if (previousShowOperatorOfferOverview !== showOperatorOfferOverview.value) {
            offerOverviewComponent.value?.redrawList();
        }
    }

    // a login or logout on this view changes the overview at once, not only the next time the view is shown
    watch(
        () => identity.hasOperatorAccess,
        () => {
            if (isViewVisible.value) updateOfferOverviewMode().catch(logError);
        }
    );

    async function onBecomeVisible(data?: { isMainNavigation: boolean; hasPreorderTimeStep?: boolean }) {
        if (!isMounted.value) {
            deferredBecomeVisibleData = data;
            return;
        }

        isViewVisible.value = true;
        TastierClient.context.onCartChanged.add(updateCart);

        await updateOfferOverviewMode();

        // update header
        updateTitle();
        updateHeaderButtonsVisibility(data);

        //replace this with less overhead
        if (needsOfferReload) {
            offerOverviewComponent.value?.setIsLoading();
            TastierClient.context.reloadOfferWithAttributes();
            needsOfferReload = false;
        } else {
            updateCart();
        }

        TastierClient.context.loadFulfillmentsIfNeeded();
        setTimeout(() => {
            displayConfirmationPopover();
        }, 500);
    }

    async function updateHeaderButtonsVisibility(
        data: { isMainNavigation: boolean; hasPreorderTimeStep?: boolean | undefined } | undefined
    ) {
        // if no info about service or is coming back from another view - do not change anything
        if (data === undefined || !data.isMainNavigation) return;

        if (data.hasPreorderTimeStep) {
            isHomeButtonVisible.value = false;
            isGoBackButtonVisible.value = true;
        } else {
            isGoBackButtonVisible.value = false;
            isHomeButtonVisible.value = true;
        }
    }

    function onBecomeInvisible() {
        isViewVisible.value = false;
        TastierClient.context.onCartChanged.remove(updateCart);
    }

    function redrawList() {
        offerOverviewComponent.value?.redrawList();
        updateCart();
    }

    function reset() {
        TastierClient.context.onCartChanged.remove(updateCart);
        needsOfferReload = true;

        if (isViewVisible.value) {
            onBecomeVisible();
        }
    }

    function updateCart() {
        const offer = TastierClient.context.currentOffer;
        let articles = 0;
        cart.setCurrentCart(offer?.cart ?? null);

        if (offer?.cart) {
            totalAmount.value = toMoneyWithSign(offer.cart.totalPayableAmount);
            if (
                offer.cart.items.length > 0 &&
                (offer.cart.totalPayableAmount >= 0 || offer.cart.totalPayableAmount < 0)
            ) {
                isPayButtonEnabled.value = true;
            } else {
                isPayButtonEnabled.value = false;
            }

            for (const cartItem of offer.cart.items) {
                articles += cartItem.quantity;
            }
        } else {
            totalAmount.value = toMoneyWithSign(0);
            isPayButtonEnabled.value = false;
        }

        selectedArticles.value = articles;

        offerOverviewComponent.value?.updateCart();
    }

    function onArticleClick(article: ICustomerArticle) {
        if (addToCardPopover === undefined) {
            addToCardPopover = new AddToCardPopover();
        }

        addToCardPopover.forArticle(article).show();
    }

    // one tap on a tile reaches this twice: the tap recognizer fires on touchend and the
    // synthetic click that follows it slips past the 350ms suppression on a slow device.
    // keyed by article so a tap on another tile still goes through while this one runs
    const quickAddInProgress = new Set<string>();

    async function quickAddItem(item: ICustomerArticleWithOptions) {
        if (quickAddInProgress.has(item.articleId)) return;
        quickAddInProgress.add(item.articleId);

        try {
            const { selectedOptions, isAddToCartAvailable, addItemToCart, fixCurrentQuantity } = useAddToCart(
                item,
                offer.orderService,
                true
            );
            fixCurrentQuantity();
            selectedOptions.value = getPreselectedOptions(item);

            if (isAddToCartAvailable.value) {
                await addItemToCart();
                // onCartItemAdded();
            } else {
                throw new Error("Add to cart is not available");
            }
        } catch (ex) {
            logError(ex);
            Popup.show({ message: trans.cart.failedToAdd });
        } finally {
            quickAddInProgress.delete(item.articleId);
        }
    }

    function onScrolledChanged(isScrolled: boolean) {
        if (isScrolled) {
            OrderController.onScrolled.dispatch();
        } else {
            OrderController.onNotScrolled.dispatch();
        }
    }

    function displayConfirmationPopover() {
        const fulfillmentBtn = document.querySelector(".fulfillmentbtn") as HTMLButtonElement | null;
        const returnFromCart = document.querySelector(".view.cart.animright") as HTMLElement | null;
        const orderOverviewPage = document.querySelector(".view.order.shown") as HTMLElement | null;
        const returnOrderHistory = document.querySelector(".view.orderhistory.animright") as HTMLElement | null;
        const returnOrderHistoryOnDone = document.querySelector(
            ".view.order.fadetransition.shown"
        ) as HTMLElement | null;

        if (
            fulfillmentBtn &&
            orderOverviewPage &&
            !returnFromCart &&
            (!returnOrderHistory || returnOrderHistoryOnDone)
        ) {
            fulfillmentBtn.click();
        }
    }

    function onGoHomeButtonClick() {
        OrderController.onShowServices.dispatch();
    }

    function onGoBackButtonClick() {
        OrderController.onGoBack.dispatch();
    }

    function onProceedButtonClick() {
        OrderController.onContinue.dispatch();
    }

    function updateTitle() {
        const serviceDesignation = Helper.getCurrentServiceDesignation();
        title.value = serviceDesignation ?? trans.order.title;
    }

    // the title is the location's own service name, translated at the moment it is read, and the
    // current service comes from TastierClient.context, which is not reactive - so it stays a ref
    // refreshed on show, and the language switch is the one change that has to push it as well
    watch(() => clientLanguage(), updateTitle);
</script>

<style lang="scss">
    @use "./../../../assets/styles/scss/shared/_colors.scss";
    @use "./../../../assets/styles/scss/shared/_mixins.scss";
    @use "./../../../assets/styles/scss/shared/_variables.scss";

    .app > .order {
        > .head {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            transition: top 0.6s variables.$easeOutExpo;
            z-index: 500;
        }

        > .index {
            height: 78px;
            position: absolute;
            top: 53px;
            left: 0;
            right: 0;
            z-index: 210;
            background-color: #fff;
            transition:
                height 0.6s variables.$easeOutExpo,
                top 0.6s variables.$easeOutExpo,
                background-color 0.2s linear;

            > .categoryslider {
                transition: height 0.6s variables.$easeOutExpo;

                > .slidable {
                    transition:
                        height 0.6s variables.$easeOutExpo,
                        padding 0.2s linear;
                }
            }
        }

        // attribute filter bar, sits below the category slider and does not scroll away. the list starts
        // below it, the strip takes no clicks of its own all the same
        > .attributefilter {
            position: absolute;
            top: 131px;
            left: 0;
            right: 0;
            z-index: 205;
            pointer-events: none;
            transition: top 0.6s variables.$easeOutExpo;

            &.has-items {
                height: 55px;
            }

            // the filter keeps the full width of the strip and inherits its pointer events: none, which
            // leaves the empty rest of the strip click-through to the list. only its row of buttons, as
            // wide as the buttons, takes clicks and drags (ArticleAttributeFilter.vue)
        }

        > .attributefilter.has-items ~ .itemlist {
            top: 186px;
        }

        // starts at the bottom edge of the fixed block above it rather than behind it, so its scrollbar can
        // not end up under that block in any browser - phones' overlay bars included. its top follows the
        // four heights of the block: 131 / 186 here, 53 / 107 in .scrolled below. OfferOverview.vue measures
        // the sections against the top of the list for that reason, and decides the collapse by the height
        // the list has with the block expanded
        > .itemlist {
            position: absolute;
            top: 131px;
            bottom: 104px;
            left: 0;
            right: 0;
            transition: top 0.6s variables.$easeOutExpo;
            z-index: 200;

            > .section {
                &:last-child {
                    padding-bottom: 32px;
                }

                > .header {
                    position: relative;

                    &.nameonly {
                        > .name {
                            border-top: 1px solid #ededed;
                            border-bottom: 1px solid #ededed;
                            background-color: #f5f5f5;
                            min-height: 54px;
                            padding: 22px 22px 6px 22px;
                            box-sizing: border-box;

                            &.twolines {
                                padding-top: 8px;
                            }

                            > h2 {
                                margin: 0;
                                font-size: 16px;
                                line-height: 20px;
                                font-weight: 400;
                                white-space: nowrap;
                                overflow: hidden;
                                text-overflow: ellipsis;
                                color: colors.$fontColor;
                            }

                            > .description {
                                font-size: 12px;
                                line-height: 16px;
                                color: colors.$lightGrey;
                            }
                        }
                    }

                    &.withimage {
                        height: 130px;

                        > .background {
                            position: absolute;
                            left: 0;
                            top: 0;
                            right: 0;
                            bottom: 0;
                            background-position: center center;
                            background-repeat: no-repeat;
                            background-size: cover;
                            z-index: 21;
                        }

                        > .blackdim {
                            position: absolute;
                            left: 0;
                            top: 0;
                            right: 0;
                            bottom: 0;
                            z-index: 22;
                            background-color: rgba(0, 0, 0, 0.5);
                        }

                        > .name {
                            position: relative;
                            z-index: 25;
                            display: flex;
                            height: 100%;
                            width: 100%;
                            flex-direction: column;
                            color: #fff;
                            text-align: center;
                            justify-content: center;
                            align-items: center;

                            > h2 {
                                margin: 0;
                                font-size: 18px;
                                font-weight: 400;
                                letter-spacing: 0.2px;
                                padding: 6px 0 6px 0;
                            }

                            > .description {
                                font-size: 12px;
                                font-weight: 300;
                            }
                        }
                    }
                }

                > .articles {
                    > .article {
                        position: relative;
                        padding: 0 22px 0 22px;

                        &.added > .name {
                            > .qty {
                                opacity: 1;
                                transform: translate3d(0, 0, 0);
                            }

                            > h3 {
                                padding-left: 28px;
                            }
                        }

                        > .item-flags {
                            margin-top: 0.5em;
                        }

                        > .name {
                            position: relative;
                            padding: 14px 68px 14px 0;

                            > .qty {
                                position: absolute;
                                left: 0;
                                top: 14px;
                                color: var(--colorPrimary) !important;
                                font-size: 16px;
                                font-weight: 600;
                                line-height: 18px;
                                display: block;
                                height: 18px;
                                width: 28px;
                                white-space: nowrap;
                                overflow: hidden;
                                text-overflow: ellipsis;
                                opacity: 0;
                                transform: translate3d(-28px, 0, 0);
                                transition:
                                    transform 0.6s variables.$easeOutExpo,
                                    opacity 0.6s variables.$easeOutExpo;
                            }

                            > h3 {
                                color: colors.$fontColor;
                                font-size: 16px;
                                font-weight: 300;
                                margin: 0;
                                line-height: 18px;
                                transition: padding 0.6s variables.$easeOutExpo;

                                &.negative-price {
                                    color: colors.$red !important;
                                }
                            }

                            > .description {
                                color: colors.$lightGrey;
                                font-size: 12px;
                                line-height: 16px;
                            }

                            // the flags follow the description, inside the padding that keeps
                            // the name clear of the price pinned to the top right
                            > .item-flags {
                                margin-top: 6px;
                            }

                            // the price hangs in the padding the name reserves on its right, so it
                            // stays on the name's line when flags push the name down the article
                            > .price {
                                position: absolute;
                                right: 4px;
                                top: 13px;

                                &.negative-price {
                                    color: colors.$red !important;
                                }
                            }
                        }

                        > .line {
                            height: 1px;
                            position: absolute;
                            background-color: #f5f5f5;
                            left: 23px;
                            right: 23px;
                            bottom: 0;
                        }
                    }

                    &.withimage {
                        > .article {
                            &.hasimage {
                                min-height: 84px;
                            }

                            > .name {
                                border: none;
                                padding-top: 11px;

                                &.with-flags  {
                                    padding-top: .5em;
                                }
                                padding-bottom: 8px;
                                padding-right: 88px;

                                > .qty {
                                    top: 11px;
                                }

                                // here the price is a block under the name, the gap below it comes
                                // from the name's own padding
                                > .price {
                                    position: relative;
                                    top: unset;
                                    right: unset;
                                    padding-top: 8px;
                                }
                            }

                            > .image {
                                position: absolute;
                                top: 50%;
                                margin-top: -37px;
                                right: 23px;
                                width: 74px;
                                height: 74px;
                                border-radius: 4px;
                                overflow: hidden;

                                > img {
                                    height: 100%;
                                    width: 100%;
                                    object-fit: contain;
                                    object-position: center;
                                }
                            }
                        }
                    }
                }
            }

            > .loading {
                position: absolute;
                left: 50%;
                white-space: nowrap;
                transform: translateX(-50%);

                > .loader {
                    background-image: url("/assets/img/app/loading_green.svg");
                }
            }

            > .nodata {
                padding: 0 20px 0 20px;

                > .lbl {
                    text-align: center;
                    font-size: 14px;
                }
            }
        }

        // the filter matched no article at all, so the list area is empty. the block spans exactly
        // what .itemlist covers - the expanded header block with the filter strip is always there in
        // this state, because an active filter means the strip has buttons
        > .nofilterresult {
            position: absolute;
            top: 186px;
            bottom: 104px;
            left: 0;
            right: 0;
            z-index: 200;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 0 20px;

            > .removefilters {
                height: 46px;
                padding: 0 24px;
                border-radius: 999px;
                border: none;
                background-color: colors.$orange;
                color: #fff;
                font-size: 17px;
                letter-spacing: 0.5px;
                cursor: pointer;

                @include mixins.hover-and-active() {
                    background-color: colors.$orangeHover;
                }
            }
        }

        > .scrolltop {
            position: absolute;
            bottom: 50px;
            right: 15px;
            background-color: var(--colorPrimary);
            z-index: 201;
            height: 44px;
            width: 44px;
            border-radius: 50%;
            text-align: center;
            cursor: pointer;
            -webkit-box-shadow: 0px 0px 7px 0px rgba(72, 72, 72, 0.11);
            -moz-box-shadow: 0px 0px 7px 0px rgba(72, 72, 72, 0.11);
            box-shadow: 0px 0px 7px 0px rgba(72, 72, 72, 0.11);
            transition: bottom 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);

            @include mixins.hover-and-active() {
                background-color: var(--colorPrimaryHover);
            }

            > .fal {
                font-size: 18px;
                color: #fff;
                line-height: 44px;
            }
        }

        &.scrolled {
            > .head {
                top: -53px;
            }

            // follows the collapsed category slider
            > .attributefilter {
                top: 52px;
            }

            // the collapsed block: the slider alone, 52px and its 1px border, or the filter strip at 52px
            // below it, 52 + 55
            > .itemlist {
                top: 53px;
            }

            > .attributefilter.has-items ~ .itemlist {
                top: 107px;
            }

            > .index {
                top: 0;
                height: 52px;
                background-color: #f5f5f5;
                border-bottom: 1px solid #dcdcdc;

                > .categoryslider {
                    height: 52px;

                    > .slidable {
                        height: 52px;
                        padding-top: 6px;
                        padding-bottom: 6px;

                        > .category:not(.active) {
                            background-color: transparent;
                        }
                    }
                }
            }

            > .scrolltop {
                bottom: 117px;
            }
        }

        > .bottom {
            height: 104px;
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
            z-index: 220;
            padding: 0 12px;
            background-color: #fff;
            -webkit-box-shadow: 0px 0px 5px 0px rgba(0, 0, 0, 0.25);
            -moz-box-shadow: 0px 0px 5px 0px rgba(0, 0, 0, 0.25);
            box-shadow: 0px 0px 5px 0px rgba(0, 0, 0, 0.25);

            > .selectionsum {
                text-align: center;
                font-size: 14px;
                color: colors.$fontColor;
                height: 38px;
                line-height: 38px;
                letter-spacing: 0.3px;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }
        }
    }
</style>
